import { useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, Stack } from "@mui/material";

import {
  CoverageMineSubsidenceScheduledItem,
  CoverageScheduledPersonalPropertyItem,
  personalPropertyTypeGraphQLToViewMap,
} from "@covertree/new-business-graphql";
import {
  ExtraCoverageAccessor,
  getMineSubsidenceOption,
  getMineSubsidenceScheduledOption,
  getRoofPaymentScheduleOption,
} from "@covertree/new-business-mappings";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { theme } from "@covertree/theme";
import { InputLabelHeight } from "@covertree/types";
import { FormSelectField, FormToggleCard, HelpText } from "@covertree/ui";
import { InfoDisclaimerBox } from "@covertree/ui/content";
import { formatCurrency, formatCustomizationOptions } from "@covertree/utils";

import {
  CustomizationValue,
  CustomizePlanCoverage,
  PolicyCustomizationForm,
  SecondaryResidenceLiabilityCustomizationCard,
} from "../..";

export interface ExtraCoverageCustomizationCardProps {
  coverage: CustomizePlanCoverage;
  stateCode: string;
  name: string;
  infoMessage?: string;
  disabled?: boolean;
}

export function ExtraCoverageCustomizationCard({
  coverage,
  name,
  disabled,
  infoMessage,
}: ExtraCoverageCustomizationCardProps) {
  const { validationKey, accessor, defaultValue, details, fieldType, options } =
    coverage;
  const { control } = useFormContext<PolicyCustomizationForm>();

  const { units } = useNewBusinessSelector(selectPolicy)!;

  const coverageOptions = useMemo(
    () => ({
      mineSubsidenceOption: getMineSubsidenceOption(units[0]),
      mineSubsidenceScheduledOption: getMineSubsidenceScheduledOption(units[0]),
      roofPaymentScheduleOption: getRoofPaymentScheduleOption(units[0]),
    }),
    [units]
  );

  const nameText = useMemo(
    () => details.getName(coverageOptions),
    [details, coverageOptions]
  );

  const tooltipText = useMemo(
    () => details.getTooltip(coverageOptions) || "",
    [details, coverageOptions]
  );

  const descriptionText = useMemo(
    () => details.getDescription(coverageOptions) || "",
    [details, coverageOptions]
  );

  const actionsContent = useCallback(
    (
      currentValue:
        | CustomizationValue
        | CoverageScheduledPersonalPropertyItem[]
        | CoverageMineSubsidenceScheduledItem[]
    ) => {
      if (validationKey === "scheduledPersonalProperty_value") {
        return (currentValue as CoverageScheduledPersonalPropertyItem[])
          ?.length > 0 ? (
          <Stack sx={{ width: "100%" }} mb={3} mt={2} spacing={1}>
            {(currentValue as CoverageScheduledPersonalPropertyItem[]).map(
              (item: CoverageScheduledPersonalPropertyItem) => (
                <Box
                  key={item.fieldGroupLocator ?? item.description}
                  role="listitem">
                  <HelpText
                    text={`${personalPropertyTypeGraphQLToViewMap[item.type]}:`}
                    spanText={formatCurrency(item.value)}
                  />
                </Box>
              )
            )}
          </Stack>
        ) : undefined;
      }

      if (validationKey === "mineSubsidenceScheduled_value") {
        return (currentValue as CoverageMineSubsidenceScheduledItem[])?.length >
          0 ? (
          <Stack sx={{ width: "100%" }} mb={3} mt={2} spacing={1}>
            {(currentValue as CoverageMineSubsidenceScheduledItem[]).map(
              (item: CoverageMineSubsidenceScheduledItem) => (
                <Box key={item.locator} role="listitem">
                  <HelpText
                    text={`${item.description}:`}
                    spanText={formatCurrency(item.value)}
                  />
                </Box>
              )
            )}
          </Stack>
        ) : undefined;
      }

      if (fieldType.includes("Dropdown")) {
        return !!currentValue ? (
          <Box width={1}>
            <FormSelectField
              id={`${validationKey}--dropdown-select`}
              dataTestId={`${validationKey}--dropdown-select`}
              name={name}
              control={control}
              label={details.valueFormats?.prefix}
              labelHeight={InputLabelHeight.SMALL}
              options={formatCustomizationOptions(options)}
              value={currentValue}
              inputStyle={{
                backgroundColor: "secondary.contrastText",
              }}
              disabled={disabled || coverage.disabled}
            />
          </Box>
        ) : undefined;
      }
      return details.valueFormats?.prefix && details.valueFormats?.value ? (
        <HelpText
          text={details.valueFormats?.prefix}
          spanText={details.valueFormats?.value}
        />
      ) : undefined;
    },
    [control, coverage, disabled]
  );

  // If any Extra Coverage needs custom behavior, we are creating separate component for it

  if (accessor === ExtraCoverageAccessor.SecondaryResidenceLiability) {
    return (
      <SecondaryResidenceLiabilityCustomizationCard
        coverage={coverage}
        name={name}
        disabled={disabled}
      />
    );
  }

  return (
    <>
      {infoMessage && (
        <InfoDisclaimerBox dataTestId="disclaimer-box-mine-subsidence" mb={1}>
          {infoMessage}
        </InfoDisclaimerBox>
      )}

      <Controller
        control={control}
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        name={name as any}
        render={({ field: { value, onChange } }) => (
          <FormToggleCard
            customizable={!coverage.disabled}
            disabled={disabled}
            title={nameText}
            image={details.image}
            tooltip={tooltipText}
            subheader={descriptionText}
            subheaderVariant="grey"
            onToggle={() => {
              onChange(
                typeof defaultValue === "boolean"
                  ? !value
                  : !!value
                    ? null
                    : defaultValue
              );
            }}
            checked={Array.isArray(value) ? value.length > 0 : !!value}
            checkedCustomStyles={{
              backgroundColor: theme.palette.secondary.contrastText,
            }}
            actions={actionsContent(value)}
          />
        )}
      />
    </>
  );
}
